import { Link } from 'react-router-dom';
import { Button } from '@/components/ui';
import { ContactLink } from '@/components/contact';

export interface ErrorPageProps {
  /** HTTP error code or custom error code */
  code: number | string;
  /** Error title */
  title: string;
  /** Detailed error message */
  message: string;
  /** Show retry button */
  showRetry?: boolean;
  /** Retry button callback */
  onRetry?: () => void;
  /** Show reload page button */
  showReload?: boolean;
  /** Reload page callback */
  onReload?: () => void;
  /** Show link to dashboard */
  showDashboardLink?: boolean;
  /** Show link to home */
  showHomeLink?: boolean;
  /** Custom action button */
  customAction?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
}

/**
 * Generic error page component for displaying various error states.
 * Provides consistent styling and navigation options.
 */
export function ErrorPage({
  code,
  title,
  message,
  showRetry = false,
  onRetry,
  showReload = false,
  onReload,
  showDashboardLink = true,
  showHomeLink = true,
  customAction,
}: ErrorPageProps) {
  return (
    <div
      className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8"
      role="main"
      aria-labelledby="error-title"
    >
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        {/* Error Code Display */}
        <h1
          className="text-9xl font-bold text-gray-200 select-none"
          aria-hidden="true"
        >
          {code}
        </h1>

        {/* Error Title */}
        <h2
          id="error-title"
          className="mt-4 text-2xl font-bold text-gray-900"
        >
          {title}
        </h2>

        {/* Error Message */}
        <p className="mt-2 text-gray-600">
          {message}
        </p>

        {/* Action Buttons */}
        <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
          {/* Retry Button */}
          {showRetry && onRetry && (
            <Button
              onClick={onRetry}
              aria-label="Try again"
            >
              Try again
            </Button>
          )}

          {/* Reload Button */}
          {showReload && onReload && (
            <Button
              variant="secondary"
              onClick={onReload}
              aria-label="Reload page"
            >
              Reload page
            </Button>
          )}

          {/* Custom Action */}
          {customAction && (
            customAction.href ? (
              <Link to={customAction.href}>
                <Button>
                  {customAction.label}
                </Button>
              </Link>
            ) : (
              <Button onClick={customAction.onClick}>
                {customAction.label}
              </Button>
            )
          )}

          {/* Home Link */}
          {showHomeLink && (
            <Link to="/">
              <Button variant={showRetry || showReload ? 'outline' : 'primary'}>
                Go home
              </Button>
            </Link>
          )}

          {/* Dashboard Link */}
          {showDashboardLink && (
            <Link to="/dashboard">
              <Button variant="outline">
                Dashboard
              </Button>
            </Link>
          )}
        </div>

        {/* Help Text - WP-CONTACT-1: Use ContactLink instead of mailto */}
        <p className="mt-8 text-sm text-gray-500">
          If this problem persists, please{' '}
          <ContactLink
            topic="support"
            context="ErrorPage"
            className="text-primary-600 hover:text-primary-500 underline cursor-pointer"
          >
            contact support
          </ContactLink>
          .
        </p>
      </div>
    </div>
  );
}

export default ErrorPage;
